import { z } from 'zod'
import type { SortingState, VisibilityState } from '@tanstack/vue-table'
import { InvoiceType, InvoiceStatusFilters } from '@/models/invoice.js'
import { PaymentMethod } from '@/models/payments.js'
import type {
  ReportFiltersState,
  ReportTotals,
} from '@/composables/useReportFiltersState.js'
import type { InvoiceReportItemModel } from '@/composables/api/InvoiceReport.js'
import type { NullableProps } from '@/utils/types.js'

// ============================================================================
// BASE TYPES
// ============================================================================

export type NormalizedInvoiceReportItem =
  NullableProps<InvoiceReportItemModel> & {
    subRows?: NormalizedInvoiceReportItem[]
  }

export type InvoiceReportPaymentStatuses =
  (typeof InvoiceReportPaymentStatus)[keyof typeof InvoiceReportPaymentStatus]
export const InvoiceReportPaymentStatus = {
  Paid: 'PAID',
  Unpaid: 'UNPAID',
  PartiallyPaid: 'PART_PAID',
  InCredit: 'IN_CREDIT',
} as const

// ============================================================================
// COLUMNS
// ============================================================================

export type InvoiceReportColumnIds =
  (typeof InvoiceReportColumns)[keyof typeof InvoiceReportColumns]['accessorKey']
export const InvoiceReportColumns = {
  InvoiceDate: {
    id: 'invoice_date',
    accessorKey: 'invoice_date',
  },
  InvoiceDueDate: {
    id: 'invoice_due_date',
    accessorKey: 'invoice_due_date',
  },
  InvoiceNumber: {
    id: 'invoice_number',
    accessorKey: 'invoice_number',
  },
  InvoiceStatus: {
    id: 'invoice_status',
    accessorKey: 'invoice_status',
  },
  InvoiceType: {
    id: 'invoice_type',
    accessorKey: 'invoice_type',
  },
  ConsultationNumber: {
    id: 'consultation_id',
    accessorKey: 'consultation_id',
  },
  ConsultationPatientType: {
    id: 'patient_type',
    accessorKey: 'patient_type',
  },
  Veterinarian: {
    id: 'supervising_vet_name_from_invoice',
    accessorKey: 'supervising_vet_name_from_invoice',
  },
  ClinicLocation: {
    id: 'department_name',
    accessorKey: 'department_name',
  },
  Client: {
    id: 'client_name',
    accessorKey: 'client_name',
  },
  CustomerId: {
    id: 'client_id',
    accessorKey: 'client_id',
  },
  TotalExclVat: {
    id: 'total_excl_vat',
    accessorKey: 'total_excl_vat',
  },
  VatAmount: {
    id: 'vat_amount',
    accessorKey: 'vat_amount',
  },
  ClientVatNumber: {
    id: 'client_vat_number',
    accessorKey: 'client_vat_number',
  },
  AmountDueInsurance: {
    id: 'amount_due_insurance',
    accessorKey: 'amount_due_insurance',
  },
  AmountDueClient: {
    id: 'amount_due_client',
    accessorKey: 'amount_due_client',
  },
  ReferringVet: {
    id: 'referring_vet_name_from_consultation',
    accessorKey: 'referring_vet_name_from_consultation',
  },
  ReferringClinic: {
    id: 'referring_org_name_from_consultation',
    accessorKey: 'referring_org_name_from_consultation',
  },
  InvoiceTags: {
    id: 'invoice_tags_list',
    accessorKey: 'invoice_tags_list',
  },
  ClientTags: {
    id: 'client_tags_list',
    accessorKey: 'client_tags_list',
  },
  TotalWithVat: {
    id: 'total_incl_vat',
    accessorKey: 'total_incl_vat',
  },
  TotalPaid: {
    id: 'amount_paid',
    accessorKey: 'amount_paid',
  },
  DueSum: {
    id: 'amount_due',
    accessorKey: 'amount_due',
  },
} as const satisfies Record<
  string,
  { id: string; accessorKey: keyof InvoiceReportItemModel }
>

/**
 * Column IDs gated behind the extended columns feature flag.
 */
export const InvoiceReportExtendedColumnIds: InvoiceReportColumnIds[] = [
  InvoiceReportColumns.CustomerId.id,
  InvoiceReportColumns.TotalExclVat.id,
  InvoiceReportColumns.VatAmount.id,
  InvoiceReportColumns.ClientVatNumber.id,
  InvoiceReportColumns.AmountDueInsurance.id,
  InvoiceReportColumns.AmountDueClient.id,
  InvoiceReportColumns.ReferringVet.id,
  InvoiceReportColumns.ReferringClinic.id,
  InvoiceReportColumns.InvoiceTags.id,
  InvoiceReportColumns.ClientTags.id,
]

/**
 * Default visible columns for invoice report
 */
export const InvoiceReportDefaultVisibleColumns = [
  InvoiceReportColumns.InvoiceDate.id,
  InvoiceReportColumns.InvoiceNumber.id,
  InvoiceReportColumns.InvoiceType.id,
  InvoiceReportColumns.ConsultationNumber.id,
  InvoiceReportColumns.ConsultationPatientType.id,
  InvoiceReportColumns.Veterinarian.id,
  InvoiceReportColumns.ClinicLocation.id,
  InvoiceReportColumns.Client.id,
  InvoiceReportColumns.CustomerId.id,
  InvoiceReportColumns.TotalExclVat.id,
  InvoiceReportColumns.VatAmount.id,
  InvoiceReportColumns.ClientVatNumber.id,
  InvoiceReportColumns.AmountDueInsurance.id,
  InvoiceReportColumns.AmountDueClient.id,
  InvoiceReportColumns.ReferringVet.id,
  InvoiceReportColumns.ReferringClinic.id,
  InvoiceReportColumns.InvoiceTags.id,
  InvoiceReportColumns.ClientTags.id,
  InvoiceReportColumns.TotalWithVat.id,
  InvoiceReportColumns.TotalPaid.id,
  InvoiceReportColumns.DueSum.id,
] as const

// ============================================================================
// GROUPING
// ============================================================================

export type InvoiceReportGroupableColumnIds = keyof Pick<
  InvoiceReportItemModel,
  | 'invoice_date'
  | 'client_name'
  | 'department_name'
  | 'supervising_vet_name_from_invoice'
  | 'referring_org_name_from_consultation'
>
export const InvoiceReportGroupableColumns = [
  'invoice_date',
  'client_name',
  'department_name',
  'supervising_vet_name_from_invoice',
  'referring_org_name_from_consultation',
] as const satisfies readonly InvoiceReportGroupableColumnIds[]

/**
 * Maps frontend field names to backend enum member names for the `report_data.group_by` parameter.
 * Used when building ShareReport and ScheduleReport payloads.
 * Backend expects enum members (CLIENT, DEPARTMENT, etc.), not field names.
 */
export const InvoiceReportGroupByFieldToEnum = {
  client_name: 'CLIENT_NAME',
  department_name: 'DEPARTMENT_NAME',
  invoice_date: 'INVOICE_DATE',
  referring_org_name_from_consultation: 'REFERRING_CLINIC',
  supervising_vet_name_from_invoice: 'SUPERVISING_VET_NAME_FROM_INVOICE',
} as const

// ============================================================================
// SORTING
// ============================================================================

/**
 * Fields that can be used for sorting in the Groups API endpoint.
 * These are aggregated fields that apply to entire groups.
 */
export const InvoiceReportGroupAllowedSortFields: string[] = [
  InvoiceReportColumns.TotalWithVat.id,
  InvoiceReportColumns.TotalPaid.id,
  InvoiceReportColumns.DueSum.id,
]

// ============================================================================
// FILTERS
// ============================================================================

export type InvoiceReportFiltersIds =
  (typeof InvoiceReportFilters)[keyof typeof InvoiceReportFilters]
export const InvoiceReportFilters = {
  GroupBy: 'groupBy',
  Search: 'search',
  InvoiceDueDate: 'invoiceDueDate',
  InvoiceIssueDate: 'invoiceIssueDate',
  Veterinarian: 'veterinarian',
  ClinicLocation: 'clinicLocation',
  Client: 'client',
  PaymentMethod: 'paymentMethod',
  InvoiceType: 'invoiceType',
  PaymentStatus: 'paymentStatus',
  InvoiceStatus: 'invoiceStatus',
  InsuranceClaimStatus: 'insuranceClaimStatus',
  Tags: 'tags',
  InvoiceTags: 'invoiceTags',
  ClientTags: 'clientTags',
  ReferringVet: 'referringVet',
} as const

/**
 * Maps filter keys to their API query param names.
 * Date filters use 'range' suffix, others use 'in' suffix.
 */
export const InvoiceReportFilterApiParam = {
  [InvoiceReportFilters.Search]: 'filter[search]',
  [InvoiceReportFilters.InvoiceDueDate]: 'filter[invoice_due_date.range]',
  [InvoiceReportFilters.InvoiceIssueDate]: 'filter[invoice_date.range]',
  [InvoiceReportFilters.Veterinarian]:
    'filter[supervising_vet_id_from_invoice.in]',
  [InvoiceReportFilters.Client]: 'filter[client_id.in]',
  [InvoiceReportFilters.ClinicLocation]: 'filter[department_id.in]',
  [InvoiceReportFilters.InvoiceType]: 'filter[invoice_type.in]',
  [InvoiceReportFilters.InvoiceStatus]: 'filter[invoice_status.in]',
  [InvoiceReportFilters.PaymentStatus]: 'filter[payment_status.in]',
  [InvoiceReportFilters.PaymentMethod]: 'filter[payment_method.in]',
  [InvoiceReportFilters.InsuranceClaimStatus]:
    'filter[insurance_claim_status.in]',
  [InvoiceReportFilters.Tags]: 'filter[tags.in]',
  [InvoiceReportFilters.InvoiceTags]: 'filter[invoice_tags]',
  [InvoiceReportFilters.ClientTags]: 'filter[client_tags]',
  [InvoiceReportFilters.ReferringVet]:
    'filter[referring_vet_id_from_consultation.in]',
} as const

/**
 * API query param names for group-specific filters used when drilling into expanded rows.
 * These accept single values to filter invoices within a specific group.
 */
export const InvoiceReportGroupFilterApiParam = {
  Client: 'filter[client_id.in]',
  ClinicLocation: 'filter[department_id.in]',
  Veterinarian: 'filter[supervising_vet_id_from_invoice.in]',
  InvoiceDate: 'filter[invoice_date.range]',
} as const

/**
 * Filter keys that represent date ranges (vs single dates or other filter types).
 * Used to determine which filters need special date formatting.
 */
export const InvoiceReportDateRangeFilters = [
  InvoiceReportFilters.InvoiceDueDate,
  InvoiceReportFilters.InvoiceIssueDate,
] as const

/**
 * Schema for invoice report filters
 */
export type InvoiceReportFiltersSchema = ReportFiltersState<
  typeof invoiceReportFiltersBaseSchema
>
export type InvoiceReportFilterLabels = Record<string, Record<string, string>>

export type InvoiceReportFiltersState = {
  filters: InvoiceReportFiltersSchema
  columnVisibility: VisibilityState
  sorting: SortingState
  totals?: ReportTotals
  filterLabels?: InvoiceReportFilterLabels
}

export const invoiceReportFiltersBaseSchema = z.object({
  [InvoiceReportFilters.GroupBy]: z.string().default(''),
  [InvoiceReportFilters.Search]: z.string().default(''),
  [InvoiceReportFilters.InvoiceDueDate]: z
    .tuple([z.string().date(), z.string().date()])
    .nullable()
    .default(null),
  [InvoiceReportFilters.InvoiceIssueDate]: z
    .tuple([z.string().date(), z.string().date()])
    .nullable()
    .default(null),
  [InvoiceReportFilters.Veterinarian]: z.array(z.string()).default([]),
  [InvoiceReportFilters.ClinicLocation]: z.array(z.string()).default([]),
  [InvoiceReportFilters.PaymentMethod]: z
    .array(z.enum(Object.values(PaymentMethod) as [string, ...string[]]))
    .default([]),
  [InvoiceReportFilters.InvoiceType]: z
    .array(z.enum(Object.values(InvoiceType) as [string, ...string[]]))
    .default([]),
  [InvoiceReportFilters.PaymentStatus]: z
    .array(
      z.enum(
        Object.values(InvoiceReportPaymentStatus) as [string, ...string[]],
      ),
    )
    .default([]),
  [InvoiceReportFilters.InvoiceStatus]: z
    .array(z.enum(Object.values(InvoiceStatusFilters) as [string, ...string[]]))
    .default([]),
  [InvoiceReportFilters.InsuranceClaimStatus]: z.array(z.string()).default([]),
  [InvoiceReportFilters.Tags]: z.array(z.string()).default([]),
  [InvoiceReportFilters.InvoiceTags]: z.array(z.string()).default([]),
  [InvoiceReportFilters.ClientTags]: z.array(z.string()).default([]),
  [InvoiceReportFilters.ReferringVet]: z.array(z.string()).default([]),
  addedFilters: z.array(z.string()).default([]),
})

// ============================================================================
// API PAYLOAD TYPES (ShareReport/ScheduleReport)
// ============================================================================

/**
 * Types for the ShareReport and ScheduleReport API's `report_data` parameter.
 *
 * The `report_data` payload tells the backend which filters, sorting, field selection,
 * and grouping to apply when generating the shared report. It uses JSON:API query
 * parameter conventions with bracket notation (e.g., `filter[field.operation]`).
 *
 * These types are defined manually because the OpenAPI schema cannot properly represent
 * this nested, dynamic structure with bracket notation in property keys.
 */
type InvoiceReportDataPayloadFilters = {
  'fields[InvoiceReport]': string
} & Partial<{
  'filter[search]': string | string[]
  'filter[invoice_due_date.range]': string
  'filter[invoice_date.range]': string
  'filter[supervising_vet_id_from_invoice.in]': string | string[]
  'filter[department_id.in]': string | string[]
  'filter[invoice_type.in]': string | string[]
  'filter[invoice_status.in]': string | string[]
  'filter[payment_status.in]': string | string[]
  'filter[invoice_tags]': string | string[]
  'filter[client_tags]': string | string[]
  'filter[referring_vet_id_from_consultation.in]': string | string[]
  sort: string
}>
type InvoiceReportDataPayloadGroupBy =
  (typeof InvoiceReportGroupByFieldToEnum)[keyof typeof InvoiceReportGroupByFieldToEnum]
export type InvoiceReportDataPayload = {
  filters: InvoiceReportDataPayloadFilters
  group_by?: InvoiceReportDataPayloadGroupBy
}

// ============================================================================
// DEFAULTS & UTILITIES
// ============================================================================

// Fields to request from the Invoice report API
export const InvoiceReportDefaultFieldsRequest = {
  InvoiceReport: [
    'invoice_date',
    'invoice_due_date',
    'invoice_id',
    'invoice_number',
    'invoice_status',
    'invoice_type',
    'total_incl_vat',
    'total_excl_vat',
    'vat_amount',
    'amount_paid',
    'amount_due',
    'amount_due_insurance',
    'amount_due_client',
    'consultation_id',
    'patient_type',
    'client_name',
    'client_id',
    'client_vat_number',
    'supervising_vet_name_from_invoice',
    'referring_vet_name_from_consultation',
    'referring_org_name_from_consultation',
    'department_name',
    'invoice_tags_list',
    'client_tags_list',
  ],
}

// Base object with all invoice report fields set to null
export const InvoiceReportEmptyItem = Object.fromEntries(
  InvoiceReportDefaultFieldsRequest.InvoiceReport.map((key) => [key, null]),
) as unknown as NormalizedInvoiceReportItem

// ============================================================================
// PREDEFINED VIEWS
// ============================================================================

const INVOICE_DATE_RANGE_FLOOR = '2000-01-01'

export const InvoiceReportPredefinedViewIds = {
  UnpaidOverdue: 'unpaid-overdue',
  VatTaxSummary: 'vat-tax-summary',
  CreditNotesRegister: 'credit-notes-register',
  RevenueByLocation: 'revenue-by-location',
} as const

/**
 * Builds column visibility state that hides all columns except the specified ones.
 */
function buildColumnVisibility(
  visibleColumns: readonly InvoiceReportColumnIds[],
): VisibilityState {
  const visible = new Set(visibleColumns)
  const visibility: VisibilityState = {}
  for (const column of Object.values(InvoiceReportColumns)) {
    if (!visible.has(column.id)) {
      visibility[column.id] = false
    }
  }
  return visibility
}

type PredefinedViewConfig = {
  id: string
  label: string
  filterState: InvoiceReportFiltersSchema
  columnVisibility: VisibilityState
  sorting: SortingState
  groupBy?: InvoiceReportGroupableColumnIds
}

export type InvoiceReportPredefinedViewDates = {
  today: string
  last30DaysStart: string
  last90DaysStart: string
  quarterStart: string
}

export type InvoiceReportPredefinedViewLabels = {
  unpaidOverdue: string
  vatTaxSummary: string
  creditNotesRegister: string
  revenueByLocation: string
}

/**
 * Builds the predefined invoice report views.
 * Date strings must be pre-computed by the caller using the project's date utilities.
 */
export function buildInvoiceReportPredefinedViews(
  dates: InvoiceReportPredefinedViewDates,
  labels: InvoiceReportPredefinedViewLabels,
): PredefinedViewConfig[] {
  const baseFilters = invoiceReportFiltersBaseSchema.parse({})

  return [
    {
      id: InvoiceReportPredefinedViewIds.UnpaidOverdue,
      label: labels.unpaidOverdue,
      filterState: {
        ...baseFilters,
        [InvoiceReportFilters.InvoiceStatus]: [InvoiceStatusFilters.Finalized],
        [InvoiceReportFilters.PaymentStatus]: [
          InvoiceReportPaymentStatus.Unpaid,
          InvoiceReportPaymentStatus.PartiallyPaid,
        ],
        [InvoiceReportFilters.InvoiceDueDate]: [
          INVOICE_DATE_RANGE_FLOOR,
          dates.today,
        ],
      },
      columnVisibility: buildColumnVisibility([
        InvoiceReportColumns.InvoiceNumber.id,
        InvoiceReportColumns.InvoiceDate.id,
        InvoiceReportColumns.InvoiceDueDate.id,
        InvoiceReportColumns.Client.id,
        InvoiceReportColumns.Veterinarian.id,
        InvoiceReportColumns.TotalWithVat.id,
        InvoiceReportColumns.AmountDueClient.id,
        InvoiceReportColumns.DueSum.id,
      ]),
      sorting: [],
    },
    {
      id: InvoiceReportPredefinedViewIds.VatTaxSummary,
      label: labels.vatTaxSummary,
      filterState: {
        ...baseFilters,
        [InvoiceReportFilters.InvoiceStatus]: [InvoiceStatusFilters.Finalized],
        [InvoiceReportFilters.InvoiceType]: [InvoiceType.CounterSale],
        [InvoiceReportFilters.InvoiceIssueDate]: [
          dates.quarterStart,
          dates.today,
        ],
      },
      columnVisibility: buildColumnVisibility([
        InvoiceReportColumns.InvoiceNumber.id,
        InvoiceReportColumns.InvoiceDate.id,
        InvoiceReportColumns.Client.id,
        InvoiceReportColumns.CustomerId.id,
        InvoiceReportColumns.ClientVatNumber.id,
        InvoiceReportColumns.TotalExclVat.id,
        InvoiceReportColumns.VatAmount.id,
        InvoiceReportColumns.TotalWithVat.id,
        InvoiceReportColumns.InvoiceType.id,
      ]),
      sorting: [],
    },
    {
      id: InvoiceReportPredefinedViewIds.CreditNotesRegister,
      label: labels.creditNotesRegister,
      filterState: {
        ...baseFilters,
        [InvoiceReportFilters.InvoiceType]: [InvoiceType.CreditNote],
        [InvoiceReportFilters.InvoiceIssueDate]: [
          dates.last90DaysStart,
          dates.today,
        ],
      },
      columnVisibility: buildColumnVisibility([
        InvoiceReportColumns.InvoiceNumber.id,
        InvoiceReportColumns.InvoiceDate.id,
        InvoiceReportColumns.Client.id,
        InvoiceReportColumns.Veterinarian.id,
        InvoiceReportColumns.ConsultationNumber.id,
        InvoiceReportColumns.TotalWithVat.id,
        InvoiceReportColumns.TotalExclVat.id,
        InvoiceReportColumns.VatAmount.id,
        InvoiceReportColumns.InvoiceTags.id,
      ]),
      sorting: [{ id: InvoiceReportColumns.Veterinarian.id, desc: false }],
      groupBy: InvoiceReportColumns.Veterinarian.id,
    },
    {
      id: InvoiceReportPredefinedViewIds.RevenueByLocation,
      label: labels.revenueByLocation,
      filterState: {
        ...baseFilters,
        [InvoiceReportFilters.InvoiceStatus]: [InvoiceStatusFilters.Finalized],
        [InvoiceReportFilters.InvoiceType]: [InvoiceType.CounterSale],
        [InvoiceReportFilters.InvoiceIssueDate]: [
          dates.last30DaysStart,
          dates.today,
        ],
      },
      columnVisibility: buildColumnVisibility([
        InvoiceReportColumns.InvoiceNumber.id,
        InvoiceReportColumns.InvoiceDate.id,
        InvoiceReportColumns.ClinicLocation.id,
        InvoiceReportColumns.Veterinarian.id,
        InvoiceReportColumns.Client.id,
        InvoiceReportColumns.TotalWithVat.id,
        InvoiceReportColumns.TotalExclVat.id,
        InvoiceReportColumns.VatAmount.id,
        InvoiceReportColumns.DueSum.id,
      ]),
      sorting: [{ id: InvoiceReportColumns.ClinicLocation.id, desc: false }],
      groupBy: InvoiceReportColumns.ClinicLocation.id,
    },
  ]
}
